
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Sparkles, TrendingUp, CheckCircle, CreditCard, Gift } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const MembershipPortalV2 = () => {
  // Fetch user's membership status
  const { data: membershipStatus } = useQuery({
    queryKey: ['user-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('membership_status, membership_tier')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  const isMember = membershipStatus?.membership_status === 'active';

  const memberBenefits = [
    {
      icon: TrendingUp,
      title: 'Member Pricing',
      description: 'Save 20-40% on all treatments with exclusive member rates'
    },
    {
      icon: Gift,
      title: 'Bonus Credits',
      description: 'Earn 2x credits on every treatment and referral'
    },
    {
      icon: Crown,
      title: 'Priority Booking',
      description: 'Skip the waitlist with priority appointment scheduling'
    },
    {
      icon: Sparkles,
      title: 'Exclusive Treatments',
      description: 'Access to member-only treatments and early launches'
    }
  ];

  const savingsCalculator = [
    {
      treatment: 'Botox (20 units)',
      retail: 400,
      member: 320,
      savings: 80
    },
    {
      treatment: 'Lip Filler (1ml)',
      retail: 650,
      member: 520,
      savings: 130
    },
    {
      treatment: 'Laser Facial',
      retail: 200,
      member: 160,
      savings: 40
    },
    {
      treatment: 'Chemical Peel',
      retail: 150,
      member: 120,
      savings: 30
    }
  ];

  const totalSavings = savingsCalculator.reduce((sum, item) => sum + item.savings, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        {isMember ? (
          <>
            <div className="flex items-center justify-center space-x-2">
              <Crown className="h-8 w-8 text-juv-terracotta" />
              <h1 className="text-4xl font-bold text-juv-deep-chestnut">
                Welcome Back, Member!
              </h1>
            </div>
            <p className="text-lg text-juv-deep-chestnut/70">
              Enjoy your exclusive benefits and member-only pricing
            </p>
            <Badge className="bg-juv-terracotta text-white px-4 py-2 text-sm">
              Active JUV Member
            </Badge>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-bold text-juv-deep-chestnut">
              JUV Membership
            </h1>
            <p className="text-lg text-juv-deep-chestnut/70">
              Unlock exclusive benefits and save on every treatment
            </p>
          </>
        )}
      </div>

      {/* Membership Benefits */}
      <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
        <CardHeader>
          <CardTitle className="text-center text-juv-deep-chestnut">
            {isMember ? 'Your Member Benefits' : 'Member Benefits'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {memberBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-juv-deep-chestnut">{benefit.title}</h3>
                  <p className="text-sm text-juv-deep-chestnut/70">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pricing & Savings Calculator */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-juv-rose-clay/20 bg-white">
          <CardHeader>
            <CardTitle className="text-juv-deep-chestnut">Member vs Non-Member Pricing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {savingsCalculator.map((item, index) => (
                <div key={index} className="p-4 border border-juv-rose-clay/20 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-juv-deep-chestnut">{item.treatment}</h4>
                    <Badge className="bg-green-100 text-green-700">
                      Save ${item.savings}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 line-through">${item.retail}</span>
                    <span className="font-bold text-juv-terracotta">${item.member}</span>
                  </div>
                </div>
              ))}
              
              <div className="p-4 bg-juv-terracotta/10 rounded-lg">
                <div className="text-center">
                  <p className="text-lg font-bold text-juv-deep-chestnut">
                    Total Potential Savings: ${totalSavings}
                  </p>
                  <p className="text-sm text-juv-deep-chestnut/70">
                    On just these 4 treatments
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-juv-rose-clay/20 bg-white">
          <CardHeader>
            <CardTitle className="text-juv-deep-chestnut">Membership Investment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-juv-terracotta mb-2">$99</div>
              <div className="text-lg text-juv-deep-chestnut">per month</div>
              <div className="text-sm text-juv-deep-chestnut/70">No long-term commitment</div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-juv-deep-chestnut">Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-juv-deep-chestnut">Immediate access to all benefits</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-juv-deep-chestnut">Works at all JUV locations</span>
              </div>
            </div>

            {!isMember && (
              <Button className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                <CreditCard className="h-4 w-4 mr-2" />
                Join JUV Membership
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Member Dashboard */}
      {isMember && (
        <Card className="border-juv-rose-clay/20 bg-white">
          <CardHeader>
            <CardTitle className="text-juv-deep-chestnut">Your Membership Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-terracotta">$0</div>
                <div className="text-sm text-juv-deep-chestnut/70">Total Saved This Month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-terracotta">0</div>
                <div className="text-sm text-juv-deep-chestnut/70">Treatments This Month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-terracotta">
                  {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </div>
                <div className="text-sm text-juv-deep-chestnut/70">Next Billing Date</div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center space-x-4">
              <Button
                variant="outline"
                className="text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
              >
                View Billing History
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Cancel Membership
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Call to Action */}
      {!isMember && (
        <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-juv-deep-chestnut mb-4">
              Ready to Save on Every Treatment?
            </h3>
            <p className="text-juv-deep-chestnut/70 mb-6 max-w-2xl mx-auto">
              Join thousands of members who are saving hundreds on their beauty treatments. 
              Cancel anytime, no long-term commitment required.
            </p>
            <Button 
              size="lg"
              className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white px-8 py-4"
            >
              <Crown className="h-5 w-5 mr-2" />
              Become a JUV Member Today
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MembershipPortalV2;
